/*
 * Collapsible Table Controller - Simplified Version
 *
 * A specialized controller for collapsible table rows with Turbo 8 morphing support and state persistence.
 * Supports multiple content targets for complex table structures.
 *
 * Features:
 * - Agnostic design - no hardcoded element IDs
 * - Turbo morphing support with automatic state restoration
 * - Multiple content targets support (e.g., multiple table rows)
 * - Value-based state persistence
 * - Improved accessibility with ARIA attributes
 * - Graceful handling of missing targets/elements
 *
 * Usage Examples:
 *
 * 1. With state persistence:
 *    <span class="hidden" data-turbo-permanent="true"
 *          data-collapsible-table-expanded-value="false"
 *          id="my_state_element"></span>
 *    <tbody data-controller="collapsible-table"
 *           data-collapsible-table-permanent-state-id-value="my_state_element">
 *      <tr>
 *        <td data-action="collapsible-table#toggle">
 *          <span data-collapsible-table-target="icon">▶</span> Toggle
 *        </td>
 *      </tr>
 *      <tr data-collapsible-table-target="content" class="hidden">Content</tr>
 *    </tbody>
 *
 * 2. Multiple content targets (table rows):
 *    <tbody data-controller="collapsible-table"
 *           data-collapsible-table-permanent-state-id-value="table_state">
 *      <tr>
 *        <td data-action="collapsible-table#toggle">
 *          <span data-collapsible-table-target="icon">▶</span> Toggle Rows
 *        </td>
 *      </tr>
 *      <tr data-collapsible-table-target="content" class="hidden">Row 1</tr>
 *      <tr data-collapsible-table-target="content" class="hidden">Row 2</tr>
 *      <tr data-collapsible-table-target="content" class="hidden">Row 3</tr>
 *    </tbody>
 *
 * 3. Simple (no persistence):
 *    <tbody data-controller="collapsible-table">
 *      <tr>
 *        <td data-action="collapsible-table#toggle">
 *          <span data-collapsible-table-target="icon">▶</span>
 *        </td>
 *      </tr>
 *      <tr data-collapsible-table-target="content" class="hidden">Content</tr>
 *    </tbody>
 *
 * Targets:
 * - content: Element(s) to show/hide (supports multiple targets)
 * - icon: Element to apply expanded class to (optional)
 *
 * Values:
 * - expanded: Boolean - Current expanded state
 * - permanentStateId: String - ID of permanent state element
 *
 * Classes:
 * - expanded: Applied to icon target when expanded
 *
 * Actions:
 * - toggle: Toggle expanded state
 * - handleKeyDown: Handle keyboard navigation (Enter/Space)
 */

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["content", "icon"]
  static values = {
    expanded: { type: Boolean, default: false },
    permanentStateId: String
  }
  static classes = ["expanded"]

  connect() {
    this.findPermanentState()
    this.restoreState()
    this.element.addEventListener("turbo:morph-element", this.handleMorph.bind(this))
    this.updateUI()
  }

  disconnect() {
    this.element.removeEventListener("turbo:morph-element", this.handleMorph.bind(this))
  }

  toggle(event) {
    event?.preventDefault()
    this.expandedValue = !this.expandedValue
  }

  handleKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.toggle(event)
    }
  }

  handleMorph() {
    this.findPermanentState()
    this.restoreState()
  }

  expandedValueChanged() {
    this.updateUI()
    this.saveState()
  }

  // Private methods
  findPermanentState() {
    this.permanentStateElement = this.hasPermanentStateIdValue
      ? document.getElementById(this.permanentStateIdValue)
      : null
  }

  restoreState() {
    if (this.permanentStateElement) {
      const savedState = this.permanentStateElement.getAttribute("data-collapsible-table-expanded-value")
      if (savedState !== null) {
        this.expandedValue = savedState === "true"
      }
    }
  }

  updateUI() {
    // Update content visibility - handle multiple content targets
    if (this.hasContentTarget) {
      this.contentTargets.forEach(target => {
        target.classList.toggle("hidden", !this.expandedValue)
      })
    }

    // Update icon state
    this.hasIconTarget && this.hasExpandedClass &&
      this.iconTarget.classList.toggle(this.expandedClass, this.expandedValue)

    // Update ARIA
    const trigger = this.element.querySelector("[aria-expanded]")
    trigger?.setAttribute("aria-expanded", this.expandedValue.toString())
  }

  saveState() {
    this.permanentStateElement?.setAttribute("data-collapsible-table-expanded-value", this.expandedValue.toString())
  }
}
